import { Address, DefaultTimestampFormat } from "@/Contracts";
import {
	AssessmentLikelihood,
	AssessmentRiskLevel,
	AssessmentSeverity
} from "@/Contracts/Assessments";
import { ClientContact } from "@/Contracts/Clients";
import {
	Document,
	DocumentStatus,
	DocumentType,
	MergeTagVariable
} from "@/Contracts/Documents";
import { CustomTemplateType } from "@/Contracts/EmailTemplates";
import {
	CustomDistributorProductDetails,
	EtilizeProductDetails,
	IcecatProductDetails,
	Product,
	ProductType,
	SortOptions
} from "@/Contracts/Products";
import {
	AddQuoteProductCatalogDrawerFormValues,
	BlockMapper,
	BlockType,
	GRID_BLOCK_DRAGGABLE,
	IMAGE_BLOCK_DRAGGABLE,
	PAGE_BREAK_BLOCK_DRAGGABLE,
	PAYMENT_SUMMARY_BLOCK_DRAGGABLE,
	QUOTE_BLOCK_DRAGGABLE,
	QuoteBlockData,
	QuoteProduct,
	SIGNATURE_BLOCK_DRAGGABLE,
	TEXT_BLOCK_DRAGGABLE,
	TextBlockData,
	VIDEO_BLOCK_DRAGGABLE
} from "@/Contracts/ProposalBuilder/Block";
import {
	CustomField,
	CustomFieldType,
	DatePatterns
} from "@/Contracts/Settings";
import {
	AllRolePermissions,
	InitializationStep,
	PricingAddOnUpdate,
	PricingPlan,
	PricingPlanEstimateData,
	PricingPlanTiers,
	TaxCategory,
	UpdatePricingPlanConstants,
	UpdatePricingPlanRequest,
	UserRole
} from "@/Contracts/Users";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import {
	capitalizeAfterUnderscore,
	customDateFormatter,
	getMatchedAddressIndexInList,
	isGivenDocumentOrderForm
} from "@/Utils";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { EWidgets } from "@organisms/ZForm/interface";
import { customCurrencyFormatter } from "@/Utils/Currency";
import DownloadFile from "downloadjs";
import filter from "lodash/filter";
import { extension } from "mime-types";
import moment from "moment";
import React from "react";
import ZModal from "@atoms/ZModal";
import { EColors } from "@atoms/ZColors/interface";
import { IOptionProps } from "@atoms/ZSelect/interface";
import colors from "@/stylesheets/colors";

import { FileOutlined, FolderAddOutlined } from "@ant-design/icons";
import { Avatar } from "antd";
import PlaceholderImage from "@/Assets/Partner/Dashboard/PlaceholderImage.svg";

const { ZText } = ZTypography;

export const MergeTagsDisplayName = (MergeTags: MergeTagVariable[]) => {
	return MergeTags.map(val => {
		return "{{" + val.display_name + "}}";
	});
};

export const nylasConnectedEmailSuffix = ":nylas";
export const formatNylasIntegratedEmailId = (
	emailId: string,
	shouldClean?: boolean
) => {
	if (shouldClean) {
		return emailId.replace(nylasConnectedEmailSuffix, "");
	} else {
		return emailId + nylasConnectedEmailSuffix;
	}
};

export const formatCustomFieldValue = (
	customField: CustomField,
	datePattern?: string
) => {
	if (customField.field_type === CustomFieldType.DateField) {
		return customField.values
			.map(value =>
				customDateFormatter(
					value,
					datePattern ?? DatePatterns.DD_HYPHEN_MMM_COMMA_YYYY
				)
			)
			.join(", ");
	}

	if (customField.field_type === CustomFieldType.DropdownFieldWithKeyValue) {
		const value = customField.allowed_dropdown_key_values?.find(
			field => field.key === customField.values[0]
		);

		return value?.value ?? "--";
	}

	return customField.values
		.map(value => capitalizeFirstLetter(value))
		.join(", ");
};

export const defaultProductTypeFormItem = {
	key: "default_for_types",
	label: "Default Product Type",
	widget: EWidgets.CHECKBOXGROUP,
	widgetProps: {
		spaceClassName: "flex-wrap",
		options: [
			{
				label: capitalizeAfterUnderscore(ProductType.Hardware),
				value: ProductType.Hardware
			},
			{
				label: capitalizeAfterUnderscore(ProductType.Service),
				value: ProductType.Service
			},
			{
				label: capitalizeAfterUnderscore(ProductType.Software),
				value: ProductType.Software
			},
			{
				label: capitalizeAfterUnderscore(ProductType.Bundle),
				value: ProductType.Bundle
			},
			{
				label: capitalizeAfterUnderscore(ProductType.Labor),
				value: ProductType.Labor
			},
			{
				label: capitalizeAfterUnderscore(ProductType.Other),
				value: ProductType.Other
			}
		]
	}
};

export const normalizeZipcode = (value: string): string =>
	value ? value.substr(0, 20).toUpperCase() : "";

// Find tier with units lying in the range
export const findTier = (units: number, planTier?: PricingPlanTiers) => {
	if (planTier) {
		const tier = planTier.tier.find(
			tier =>
				units >= tier.starting_unit &&
				(units <= tier.ending_unit || tier.ending_unit === 0)
		);
		return tier;
	} else return;
};

export const constructSubscriptionChangeMessage = (
	action: string,
	data: UpdatePricingPlanRequest,
	planData?: { currentPlan?: PricingPlan; activePlanTier?: PricingPlanTiers },
	updatedPlan?: PricingPlan
) => {
	const isCurrentPlan = planData?.currentPlan?.current_plan;
	if (action === UpdatePricingPlanConstants.UPGRADED) {
		if (planData?.activePlanTier?.period !== data.period && isCurrentPlan) {
			return `Successfully changed to ${data.period} plan`;
		} else {
			return `Successfully upgraded to ${capitalizeFirstLetter(
				updatedPlan?.name
			)} plan`;
		}
	} else if (action === UpdatePricingPlanConstants.DOWNGRADED) {
		if (planData?.activePlanTier?.period !== data.period && isCurrentPlan) {
			return `Successfully changed to ${data.period} plan`;
		} else {
			return `Successfully changed to ${capitalizeFirstLetter(
				updatedPlan?.name
			)} plan`;
		}
	} else if (action === UpdatePricingPlanConstants.ADD_ADDON) {
		return `Add-on(s) are added successfully`;
	} else if (action === UpdatePricingPlanConstants.REMOVE_ADDON) {
		return `Add-on(s) are removed successfully`;
	}

	return "";
};

export const getUpdatePricingPlanMessages = (
	currentPlan: PricingPlan,
	subscriptionEstimate: PricingPlanEstimateData,
	planUpdate: string,
	planTier: string,
	isSeatAddition: boolean,
	isRemoveSeat: boolean,
	addOn?: string,
	currentPlanFromState?: PricingPlan
) => {
	const currentPlanTier = currentPlan.plan_tiers.find(
		planTier => planTier.active
	);
	const nextBillingDate = moment(
		subscriptionEstimate.next_billing_date
	).format(DefaultTimestampFormat);
	const nextBillingAmount = customCurrencyFormatter(
		subscriptionEstimate.next_billing_amount,
		currentPlan.currency
	);
	const proratedAmount = customCurrencyFormatter(
		subscriptionEstimate.prorated_amount,
		currentPlan.currency
	);
	const creditNotes = subscriptionEstimate.credit_notes;
	const creditNoteTotal = creditNotes?.reduce(
		(acc, note) => acc + note.total,
		0
	);

	const nextBillingDateText = (
		<ZText colorVariant={8} weight={EFontWeights.MEDIUM}>
			{nextBillingDate}
		</ZText>
	);
	const nextBillingAmountText = (
		<ZText colorVariant={8} weight={EFontWeights.MEDIUM}>
			{nextBillingAmount}
		</ZText>
	);
	const proratedAmountText = (
		<ZText colorVariant={8} weight={EFontWeights.MEDIUM}>
			{proratedAmount}
		</ZText>
	);
	const creditNoteAmountAvailableText = (
		<ZText colorVariant={8} weight={EFontWeights.MEDIUM}>
			{customCurrencyFormatter(creditNoteTotal, currentPlan.currency)}
		</ZText>
	);

	if (
		(planUpdate === UpdatePricingPlanConstants.UPGRADE || isSeatAddition) &&
		(planTier === currentPlanTier?.period ||
			currentPlan.hierarchy !== currentPlanFromState?.hierarchy)
	) {
		return (
			<>
				Approximate prorated invoice of {proratedAmountText} will be
				generated immediately. An invoice of {nextBillingAmountText}{" "}
				will be generated for the next billing cycle on{" "}
				{nextBillingDateText}
			</>
		);
	} else if (
		((planUpdate === UpdatePricingPlanConstants.DOWNGRADE ||
			isRemoveSeat) &&
			planTier === currentPlanTier?.period) ||
		currentPlanFromState?.hierarchy !== currentPlan.hierarchy
	) {
		return (
			<>
				Approximate Prorated credits of {creditNoteAmountAvailableText}{" "}
				will be added to your account. These credits will be adjusted in
				your next billing cycle. An invoice of {nextBillingAmountText}{" "}
				will be generated for the next billing cycle on{" "}
				{nextBillingDateText}
			</>
		);
	} else if (
		planTier !== currentPlanTier?.period &&
		planUpdate === UpdatePricingPlanConstants.UPGRADE
	) {
		return (
			<>
				Approximate prorated invoice of {proratedAmountText} will be
				generated immediately. An invoice of {nextBillingAmountText}{" "}
				will be generated for the next billing cycle on{" "}
				{nextBillingDateText}
			</>
		);
	} else if (
		planTier !== currentPlanTier?.period &&
		currentPlanFromState?.hierarchy === currentPlan.hierarchy &&
		planUpdate === UpdatePricingPlanConstants.DOWNGRADE
	) {
		return <>Contact support for downgrading</>;
	} else if (addOn === PricingAddOnUpdate.ADD) {
		return (
			<>
				You will be charged {proratedAmountText} immediately. Your
				future {currentPlanTier?.period} recurring cost will be{" "}
				{nextBillingAmountText}
			</>
		);
	} else if (addOn === PricingAddOnUpdate.REMOVE) {
		return (
			<>
				The add-on will be removed at the end of current billing cycle.
				Your {planTier} cost will be {nextBillingAmountText}
			</>
		);
	} else return "";
};

// Pricing is sorted in ascending order.
export const getPreferredPricingForProduct = (product: Product) =>
	product.pricing[0]?.id;

export const constructProductIdToPricingMapping = (
	products: Product[]
): Record<string, string> => {
	return products.reduce(
		(total, product) => ({
			...total,
			[product.id]: getPreferredPricingForProduct(product)
		}),
		{}
	);
};

export const selectPricingBasedOnSort = (
	products: Product[],
	sortBy?: SortOptions
): Record<string, string | null> => {
	return products.reduce((map, product) => {
		const pricingList = [...product.pricing];

		if (!pricingList.length) {
			map[product.id] = null;
			return map;
		}

		let selectedPricing;

		if (sortBy === SortOptions.SellPriceHighToLow) {
			selectedPricing = pricingList.sort(
				(a, b) => (b.sell_price ?? 0) - (a.sell_price ?? 0)
			)[0];
		} else if (sortBy === SortOptions.SellPriceLowToHigh) {
			selectedPricing = pricingList.sort(
				(a, b) =>
					(a.sell_price ?? Infinity) - (b.sell_price ?? Infinity)
			)[0];
		} else if (sortBy === SortOptions.CostPriceHighToLow) {
			selectedPricing = pricingList.sort(
				(a, b) => (b.cost_price ?? 0) - (a.cost_price ?? 0)
			)[0];
		} else if (sortBy === SortOptions.CostPriceLowToHigh) {
			selectedPricing = pricingList.sort(
				(a, b) =>
					(a.cost_price ?? Infinity) - (b.cost_price ?? Infinity)
			)[0];
		} else {
			// Default: use the first pricing plan. flattenPricingResponseToProducts
			// places the matched item view's pricing first, so this selects it
			// without clobbering that order with a cross-product sort.
			selectedPricing = pricingList[0];
		}

		map[product.id] = selectedPricing?.id ?? null;
		return map;
	}, {} as Record<string, string | null>);
};

export const areRequiredFieldsFilledForAddProductToQuoteBlockForm = (
	quoteProductFormValues:
		| AddQuoteProductCatalogDrawerFormValues
		| QuoteProduct,
	taxCategoriesList: TaxCategory[]
) => {
	const isTaxCategoryDisabled =
		taxCategoriesList.find(
			taxCategory =>
				taxCategory.id === quoteProductFormValues.tax_category_id
		)?.disabled ?? true;

	const areNonCustomFieldsUnfilled =
		!quoteProductFormValues.name ||
		!quoteProductFormValues.product_type ||
		!quoteProductFormValues.tax_category_id ||
		!quoteProductFormValues.quantity ||
		isTaxCategoryDisabled;
	if (areNonCustomFieldsUnfilled) {
		return false;
	}
	for (const customField of quoteProductFormValues.custom_fields ?? []) {
		if (
			customField.mandatory_field_details?.for_ui &&
			!customField.values
		) {
			return false;
		}
	}
	return true;
};

export const getSeverityLikelihoodFromRisk = (risk: AssessmentRiskLevel) => {
	switch (risk) {
		case AssessmentRiskLevel.LOW:
			return {
				severity: AssessmentSeverity.ACCEPTABLE,
				likelihood: AssessmentLikelihood.UNLIKELY
			};
		case AssessmentRiskLevel.MEDIUM:
			return {
				severity: AssessmentSeverity.TOLERABLE,
				likelihood: AssessmentLikelihood.POSSIBLE
			};
		case AssessmentRiskLevel.HIGH:
			return {
				severity: AssessmentSeverity.INTOLERABLE,
				likelihood: AssessmentLikelihood.HIGHLY_LIKELY
			};
		default:
			return {
				severity: AssessmentSeverity.ACCEPTABLE,
				likelihood: AssessmentLikelihood.UNLIKELY
			};
	}
};

export const getRiskFromSeverityLikelihood = ({
	severity,
	likelihood
}: {
	severity: AssessmentSeverity;
	likelihood: AssessmentLikelihood;
}) => {
	switch (severity) {
		case AssessmentSeverity.INTOLERABLE:
			return likelihood === AssessmentLikelihood.UNLIKELY
				? AssessmentRiskLevel.MEDIUM
				: AssessmentRiskLevel.HIGH;
		case AssessmentSeverity.TOLERABLE:
			return likelihood === AssessmentLikelihood.UNLIKELY
				? AssessmentRiskLevel.LOW
				: AssessmentRiskLevel.MEDIUM;
		case AssessmentSeverity.ACCEPTABLE:
			return likelihood === AssessmentLikelihood.HIGHLY_LIKELY
				? AssessmentRiskLevel.MEDIUM
				: AssessmentRiskLevel.LOW;
	}
};

export const getDefaultEmails = (companyUserList: any[], userCompany?: any) => {
	const defaultUsers: string[] = [];
	companyUserList.forEach(user => {
		const defaultUserEmail = userCompany
			? userCompany.settings.preference_settings
					?.document_preference_settings?.default_from_address
			: user?.user_settings?.preference_settings
					?.document_preference_settings?.default_from_address;
		if (
			defaultUserEmail &&
			Array.isArray(defaultUsers) &&
			!defaultUsers?.includes(defaultUserEmail)
		)
			defaultUsers.push(defaultUserEmail);
	});
	return defaultUsers;
};

export const downloadReceivedFile = (
	fileImportResponse: Blob,
	fileName: string
) => {
	const ext = extension(fileImportResponse.type);
	DownloadFile(fileImportResponse, [fileName, ext].join("."));
};
export const autoPopulateEmailSubjectBasedOnTemplateType = (
	overridingTemplateType: CustomTemplateType | null
) => {
	switch (overridingTemplateType) {
		case CustomTemplateType.SendDocument: {
			return `<p><span class="merge-tag mceNonEditable" contenteditable="false"> {{Document.Owner.Name}}</span> has sent <span class="merge-tag mceNonEditable" contenteditable="false">{{Document.Name}}</span> document</p>`;
		}
		case CustomTemplateType.DocumentReminder: {
			return `<p>Reminder to approve <span class="merge-tag mceNonEditable" contenteditable="false">{{Document.Name}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentExpired: {
			return `<p>Expiry Reminder: <span class="merge-tag mceNonEditable" contenteditable="false">{{Document.Name}}</span> document</p>`;
		}
		case CustomTemplateType.DocumentWon: {
			return `<p>All participants have approved the document <span class="merge-tag mceNonEditable" contenteditable="false">{{Document.Name}}</span></p>`;
		}
		case CustomTemplateType.SignDocument: {
			return `<p><span class="merge-tag mceNonEditable" contenteditable="false">{{Document.Name}}</span> document is ready for your approval</p>`;
		}
		default:
			return "";
	}
};

export const autoPopulateEmailBodyBasedOnTemplateType = (
	overridingTemplateType: CustomTemplateType | null
) => {
	switch (overridingTemplateType) {
		case CustomTemplateType.SendDocument: {
			return `<p><span class="merge-tag mceNonEditable">{{Sender.FirstName}}</span> has sent you <span class="merge-tag mceNonEditable">{{Document.Name}}</span> .</p>
				<p>The Document will Expire on: <span class="merge-tag mceNonEditable">{{Document.ExpiresAt}}</span>&nbsp;</p>
				<p>View Document here: <span class="merge-tag mceNonEditable">{{Receiver.Document.Url}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentReminder: {
			return `<p>The document <span class="merge-tag mceNonEditable">{{Document.Name}}</span> is waiting for your approval.</p>
				<p>To review and approve the document click on the below link.</p>
				<p><span class="merge-tag mceNonEditable">{{Receiver.Document.Url}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentExpired: {
			return `<p>The document <span class="merge-tag mceNonEditable">{{Document.Name}}</span> is available for approval until <span class="merge-tag mceNonEditable">{{Document.ExpiresAt}}</span> and will expire thereafter.</p>
				<p>Please review and approve the document or contact the sender for more information.</p>
				<p>View Document: <span class="merge-tag mceNonEditable">{{Receiver.Document.Url}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentWon: {
			return `<p>All Participants have signed and approved the <span class="merge-tag mceNonEditable">{{Document.Name}}</span> document.</p>
				<p>The signed copy of the document is attached in this email for your reference.</p>`;
		}
		case CustomTemplateType.SignDocument: {
			return `<p><span class="merge-tag mceNonEditable">{{Document.Name}}</span> document is ready for your approval.</p>
				<p>You can approve the Document here: <span class="merge-tag mceNonEditable">{{Receiver.Document.Url}}</span></p>`;
		}
		default:
			return "";
	}
};

export const autoPopulateEmailEndSalutationBasedOnTemplateType = (
	overridingTemplateType: CustomTemplateType | null
) => {
	switch (overridingTemplateType) {
		case CustomTemplateType.SendDocument: {
			return `<p>Thanks,</p>
			<p><span class="merge-tag mceNonEditable">{{Sender.FirstName}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentReminder: {
			return `<p>Thanks,</p>
			<p><span class="merge-tag mceNonEditable">{{Sender.FirstName}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentExpired: {
			return `<p>Thanks,</p>
			<p><span class="merge-tag mceNonEditable">{{Document.Owner.Name}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentWon: {
			return `<p>Thank you,</p>
			<p><span class="merge-tag mceNonEditable">{{MyCompany.Name}}</span></p>`;
		}
		case CustomTemplateType.SignDocument: {
			return `<p>Thanks,</p>
			<p><span class="merge-tag mceNonEditable">{{MyCompany.Name}}</span></p>`;
		}
		default:
			return "";
	}
};

export const autoPopulateEmailHeadingBasedOnTemplateType = (
	overridingTemplateType: CustomTemplateType | null
) => {
	switch (overridingTemplateType) {
		case CustomTemplateType.SendDocument: {
			return `<p>Hey <span class="merge-tag mceNonEditable">{{Receiver.FirstName}}</span> ,</p>`;
		}
		case CustomTemplateType.DocumentReminder: {
			return `<p>Hey <span class="merge-tag mceNonEditable">{{Receiver.FirstName}}</span> ,</p>`;
		}
		case CustomTemplateType.DocumentExpired: {
			return `<p>Hey <span class="merge-tag mceNonEditable">{{Receiver.FirstName}}</span>&nbsp;</p>`;
		}
		case CustomTemplateType.DocumentWon: {
			return `<p>Congratulations!</p>`;
		}
		case CustomTemplateType.SignDocument: {
			return `<p>Hi <span class="merge-tag mceNonEditable">{{Receiver.FirstName}}</span></p>`;
		}
		default:
			return "";
	}
};

export const getAdminRoleState = (): any => {
	const defaultAdminData: any = {};
	defaultAdminData.name = {
		first: "Abhishek",
		last: "Kumar",
		full: "Abhishek Kumar"
	};
	defaultAdminData.email = "abhishek@zomentum.com";
	defaultAdminData.company_id = "1ec9a1b3e87d4b9a841d16ed3d11349f";
	defaultAdminData.id = "3ea4ce4aa5f948d8b2822adea321a3bc";
	defaultAdminData.role = UserRole.PrimaryRole;
	defaultAdminData.access_level = "company_level";
	defaultAdminData.roles_permissions = {
		role: "admin",
		permissions: Object.values(AllRolePermissions)
	};
	return defaultAdminData;
};

export const isGivenDocumentRequestedQuote = (
	currentActiveDocument?: Document | null
) => {
	const { document_type, status } = currentActiveDocument || {};
	return (
		document_type === DocumentType.QUOTE &&
		status === DocumentStatus.Requested
	);
};

export const checkMandatoryDocumentSystemFields = (
	currentActiveDocument: Document
) => {
	const isOrderForm = isGivenDocumentOrderForm(currentActiveDocument);
	return (
		!currentActiveDocument?.name?.length ||
		!(currentActiveDocument?.recipients?.length || isOrderForm) ||
		!(currentActiveDocument?.client_company_id?.length || isOrderForm) ||
		(!currentActiveDocument?.tax_region_id?.length &&
			!!currentActiveDocument.revisions?.filter(revision =>
				revision.blocks?.data?.some(data => {
					const quoteBlockData = data.data as QuoteBlockData;
					return quoteBlockData?.sections?.some(
						section => !!section?.products?.length
					);
				})
			)?.length) ||
		!currentActiveDocument?.user_id?.length
	);
};

export const getPropertyByPath = (object: any, path: string, fallback = "") => {
	const fragments = path.split(".");
	for (let i = 0; i < fragments.length; i++) {
		if (!Object.prototype.hasOwnProperty.call(object, fragments[i])) {
			return fallback;
		}
		object = object[fragments[i]];
	}
	return object;
};

export const getSigneesAndInputFieldsWithInactiveUser = (
	blockMapper: BlockMapper,
	currentActiveDocument: Document | null,
	inactiveUserIds: string[]
) => {
	const signBlocksWithInactiveSignees = filter(blockMapper, block => {
		return (
			block.type === BlockType.SIGNATURE &&
			!!currentActiveDocument?.recipients.find(recipient => {
				return (
					Array.isArray(recipient.blocks_to_sign) &&
					recipient.blocks_to_sign?.includes(block.id) &&
					Array.isArray(inactiveUserIds) &&
					inactiveUserIds?.includes(recipient.id)
				);
			})
		);
	});
	const textBlocksWithInactiveSignees = Object.values(blockMapper).filter(
		block => {
			const blockData = block?.data as TextBlockData;
			const html = document
				.createRange()
				.createContextualFragment(blockData?.text);
			const inputFieldElements = html.querySelectorAll(
				'[class="input-field mceNonEditable"]'
			);
			return (
				block.type === BlockType.TEXT &&
				Array.from(inputFieldElements).some(inputFieldElement => {
					const htmlElement = inputFieldElement as HTMLElement;
					const recipientId = htmlElement.dataset.fieldAssignee;
					return (
						Array.isArray(inactiveUserIds) &&
						inactiveUserIds?.includes(recipientId ?? "")
					);
				})
			);
		}
	);

	return {
		signBlocksWithInactiveSignees,
		textBlocksWithInactiveSignees
	};
};

export const handleDeleteShippingAddress = (
	deleteSelectedShippingAddress: Address,
	index: number,
	setShippingAddresses: (value: React.SetStateAction<Address[]>) => void,
	content: string
): void => {
	ZModal.confirm({
		title: "Are you sure you want to delete this Shipping Address?",
		content,
		onOk: () => {
			setShippingAddresses(prevState =>
				deleteSelectedShippingAddress.is_default
					? [
							...prevState
								.filter((_, idx) => idx !== index)
								?.map((address, idx) =>
									idx === 0
										? {
												...address,
												is_default: true
										  }
										: address
								)
					  ]
					: [...prevState.filter((_, idx) => idx !== index)]
			);
		},
		okType: "danger",
		okText: "Delete"
	});
};

export const shippingAddressEditHandler = (
	editSelectedShippingAddress: Address,
	shippingAddresses: Address[],
	index: number
): Address[] => {
	let updatedShippingAddresses = [...shippingAddresses];
	editSelectedShippingAddress.address_line_2 =
		editSelectedShippingAddress.address_line_2 ?? "";
	if (editSelectedShippingAddress.is_default) {
		updatedShippingAddresses = shippingAddresses?.filter(
			(_, idx) => idx !== index
		);
		updatedShippingAddresses = updatedShippingAddresses?.map(address => ({
			...address,
			is_default: false
		}));
		updatedShippingAddresses.unshift(editSelectedShippingAddress);
	} else {
		updatedShippingAddresses = shippingAddresses?.map((address, idx) =>
			idx === index ? editSelectedShippingAddress : address
		);
	}
	return updatedShippingAddresses;
};

export const shippingAddressAddHandler = (
	addSelectedShippingAddress: Address,
	shippingAddresses: Address[]
): { updatedShippingAddresses: Address[]; selectedIndex: number } => {
	let updatedShippingAddresses;
	addSelectedShippingAddress.address_line_2 =
		addSelectedShippingAddress.address_line_2 ?? "";
	if (!shippingAddresses.length) {
		addSelectedShippingAddress.is_default = true;
	} else if (addSelectedShippingAddress?.is_default) {
		const newShippingAddresses = shippingAddresses.map(address => {
			if (address.is_default) {
				const newAddress = { ...address };
				newAddress.is_default = false;
				return newAddress;
			}
			return address;
		});
		newShippingAddresses.unshift(addSelectedShippingAddress);
		updatedShippingAddresses = [...newShippingAddresses];
		return { updatedShippingAddresses, selectedIndex: 0 };
	}
	updatedShippingAddresses = [
		...shippingAddresses.concat(addSelectedShippingAddress)
	];
	return {
		updatedShippingAddresses,
		selectedIndex: updatedShippingAddresses.length - 1
	};
};

export const hasDuplicateAddressInList = (
	addressList: Address[],
	searchAddress: Address
) => {
	return getMatchedAddressIndexInList(addressList, searchAddress) > -1;
};

export const isValidAddress = (address: Address): boolean => {
	return (
		!address.address_line_1?.trim().length &&
		!address.address_line_2?.trim().length &&
		!address.state?.trim().length &&
		!address.city?.trim().length &&
		!address.country?.trim().length &&
		!address.pincode?.trim().length
	);
};

export const setupPending = (value: {
	company_initialization_step?: InitializationStep;
	user_initialization_step?: InitializationStep;
}): boolean => {
	const isCompanyDetailsNotCaptured =
		!!value.company_initialization_step &&
		value.company_initialization_step !==
			InitializationStep.CompanyDetailsCaptured;

	const isPaymentScreenNotPresented =
		!!value.company_initialization_step &&
		value.company_initialization_step !==
			InitializationStep.PaymentScreenPresented;

	const isPasswordResetNotDone =
		!!value.user_initialization_step &&
		value.user_initialization_step !== InitializationStep.PasswordResetDone;

	// In signup flow v3, if the company details are not captured & card details are not filled OR password is not set, go back to setup.
	return (
		(isCompanyDetailsNotCaptured && isPaymentScreenNotPresented) ||
		isPasswordResetNotDone
	);
};

export const setupDone = (value: {
	company_initialization_step?: InitializationStep;
	user_initialization_step?: InitializationStep;
}): boolean => {
	const isCompanyDetailsCaptured =
		value.company_initialization_step ===
		InitializationStep.CompanyDetailsCaptured;

	const isPaymentScreenPresented =
		value.company_initialization_step ===
		InitializationStep.PaymentScreenPresented;

	const isPasswordResetDone =
		value.user_initialization_step === InitializationStep.PasswordResetDone;

	// In signup flow v3, password is reset && company details are captured OR card details are filled, setup is done.
	return (
		(isCompanyDetailsCaptured || isPaymentScreenPresented) &&
		isPasswordResetDone
	);
};

export const timeAgo = (date: Date): string => {
	const seconds = Math.floor((+new Date() - +date) / 1000);

	let interval = seconds / 31536000;

	if (interval > 1) {
		return Math.floor(interval) + " years";
	}
	interval = seconds / 2592000;
	if (interval > 1) {
		return Math.floor(interval) + " months";
	}
	interval = seconds / 86400;
	if (interval > 1) {
		return Math.floor(interval) + " days";
	}
	interval = seconds / 3600;
	if (interval > 1) {
		return Math.floor(interval) + " hours";
	}
	interval = seconds / 60;
	if (interval > 1) {
		return Math.floor(interval) + " minutes";
	}
	return Math.floor(seconds) + " seconds";
};

export const getHexColorCode = (
	color: EColors,
	colorVariant: number
): string => {
	if (color === EColors.TRANSPARENT) return "#00000000";
	const colorIndex =
		colorVariant >= 1 && colorVariant <= 10 ? colorVariant - 1 : 10;
	return colors[color][colorIndex];
};

export const getClientContactRow = (
	contacts: ClientContact[]
): IOptionProps[] => {
	return contacts?.map(contact => ({
		name: capitalizeFirstLetter(
			contact?.name
				? `${contact?.name.full} ${
						contact?.email ? `<${contact?.email}>` : ""
				  }`
				: ""
		),
		value: contact?.id
	}));
};

export const handleAvoCall = (entityId: string) => {
	switch (entityId) {
		case GRID_BLOCK_DRAGGABLE: {
			break;
		}
		case IMAGE_BLOCK_DRAGGABLE: {
			break;
		}
		case TEXT_BLOCK_DRAGGABLE: {
			break;
		}
		case QUOTE_BLOCK_DRAGGABLE: {
			break;
		}
		case SIGNATURE_BLOCK_DRAGGABLE: {
			break;
		}
		case VIDEO_BLOCK_DRAGGABLE: {
			break;
		}
		case PAGE_BREAK_BLOCK_DRAGGABLE: {
			break;
		}
		case PAYMENT_SUMMARY_BLOCK_DRAGGABLE: {
			break;
		}
		default: {
			return;
		}
	}
};

export const getVendorProducts = (
	productsWithoutPricing: Product[],
	isIcecatEnabled: boolean
) => {
	if (isIcecatEnabled) {
		return productsWithoutPricing.map(product => {
			const icecat_product = new IcecatProductDetails();
			icecat_product.id = product.id;
			icecat_product.icecat_skus_info = product?.icecat_skus_info;
			icecat_product.manufacturer_part_no = product?.manufacturer_part_no;
			return icecat_product;
		});
	} else {
		return productsWithoutPricing.map(product => {
			const etilize_poduct = new EtilizeProductDetails();
			etilize_poduct.id = product.id;
			etilize_poduct.etilize_skus_info = product?.etilize_skus_info;
			etilize_poduct.manufacturer_part_no = product?.manufacturer_part_no;
			return etilize_poduct;
		});
	}
};

export const getCustomDistributorProducts = (
	productsWithoutPricing: Product[]
) => {
	return productsWithoutPricing.map(product => {
		const custom_distributor_product =
			new CustomDistributorProductDetails();
		custom_distributor_product.id = product.id;
		custom_distributor_product.manufacturer_part_no =
			product?.manufacturer_part_no;
		return custom_distributor_product;
	});
};

export const isIOSDevice = () => {
	const userAgent = navigator.userAgent || navigator.vendor;
	return /iPad|iPhone|iPod/.test(userAgent);
};
export const getFileExtension = (fileName: string): string => {
	const parts = fileName.split(".");
	return parts.length > 1 ? parts[parts.length - 1].toLocaleUpperCase() : "";
};
export const getFileTypeIcons = (fileName: string): JSX.Element => {
	const extension = getFileExtension(fileName);
	switch (extension) {
		case "PDF":
		case "DOCX":
		case "DOC":
			return (
				<Avatar
					className="mr-3"
					style={{ backgroundColor: "#E2EBFF" }}
					size="large"
					icon={<FileOutlined className="text-3 text-blue-6" />}
				/>
			);
		case "PNG":
		case "JPEG":
		case "JPG":
			return (
				<div>
					<PlaceholderImage className="mr-3" />
				</div>
			);
		default:
			return (
				<Avatar
					className="mr-3"
					style={{ backgroundColor: "#E2EBFF" }}
					size="large"
					icon={<FolderAddOutlined className="text-3 text-blue-6" />}
				/>
			);
	}
};
export const getOwner = (companyUserList: any[]) => {
	const owner: any | undefined = companyUserList.find(user => user.is_owner);
	if (owner) {
		return owner;
	}
	let firstCreatedUser: any = companyUserList[0];
	companyUserList.forEach(user => {
		if (user.created <= firstCreatedUser.created) {
			firstCreatedUser = user;
		}
	});
	return firstCreatedUser;
};
